import type { Call } from '@twilio/voice-sdk';
import type { AnyAction, Dispatch } from 'redux';

import { twilioActions } from '../../actions/twilioActions';
import type { CallResult, TwilioRuntime } from '../runtime';
import type { TwilioEventNotifiers } from '../types';

import type { TwilioCommandExecutor } from './commandExecutor';
import type { RegionResolver } from './regionResolver';
import type { TwilioActionHandler } from './types';

const {
  twilioMakeCall,
  twilioMakeCallFailed,
  twilioMakeInternalCall,
  twilioMakeInternalCallFailed,
  twilioMakeCallback,
  twilioMakeCallbackFailed,
  twilioInit,
  twilioSendDigits,
  twilioClose,
  twilioConnectCall,
  twilioConnectCallFailed,
  twilioConnectCallSucceeded,
  twilioDisconnectCall,
  twilioDisconnectCallSucceeded,
  twilioDisconnectCallFailed,
  twilioFeedback,
  twilioAddActiveCall,
  twilioMakeCallSucceeded,
  twilioMakeInternalCallSucceeded,
  twilioMakeCallbackSucceeded,
} = twilioActions;

// Sent as the failure reason when a region token cannot be resolved before a
// call attempt, letting callers distinguish this specific pre-flight failure
// from a general SDK error.
const missingRegionReason = 'missing_region' as const;

// Everything an action handler needs but that isn't available at module load
// time — injected at middleware setup so handlers stay pure functions with no
// module-level singletons.
type ActionHandlerDeps = {
  dispatch: Dispatch<AnyAction>;
  runtime: TwilioRuntime;
  handlers: TwilioEventNotifiers;
  executor: TwilioCommandExecutor;
  region: RegionResolver;
  resetRegion: () => void;
};

// Builds the action-type → handler map for the Twilio middleware. Each handler
// translates a Redux action into a TwilioRuntime command and dispatches the
// appropriate success/failure action when the command settles. All side-effectful
// dependencies (runtime, executor, region resolver) are passed in so the
// function remains testable without module-level mocking.
export function createActionHandlers({
  dispatch,
  runtime,
  handlers,
  executor,
  region,
  resetRegion,
}: ActionHandlerDeps): Partial<Record<string, TwilioActionHandler>> {
  const {
    executeTwilioCommand,
    executeRegionAwareTwilioCommand,
    readCsidFromAction,
  } = executor;
  const { resolveOutboundTokenRegion, resolveConversationLedTokenRegion } =
    region;

  // Outbound call to an external number. Region is derived from the from-number
  // so the SDK device is initialised in the right Twilio region before dialling.
  // `beforeRun` optimistically adds the call to the active-calls list so the UI
  // shows it immediately without waiting for the SDK to confirm.
  const handleMakeCall: TwilioActionHandler = (action) => {
    const { userId, fromNumber, toNumber } = action.payload;

    executeRegionAwareTwilioCommand<CallResult>({
      action,
      resolveRegion: () => resolveOutboundTokenRegion(fromNumber as string),
      onMissingRegion: () =>
        dispatch(twilioMakeCallFailed({ reason: missingRegionReason })),
      beforeRun: () =>
        dispatch(
          twilioAddActiveCall(
            userId,
            fromNumber,
            toNumber,
            'outbound',
            Date.now(),
          ),
        ),
      run: () =>
        runtime.makeCall({
          userId: userId as string,
          fromNumber: fromNumber as string,
          toNumber: toNumber as string,
        }),
      onSucceeded: (result) =>
        dispatch(
          twilioMakeCallSucceeded({
            status: result.status,
            direction: result.direction,
            userId,
            from: fromNumber,
          }),
        ),
      onFailed: () => dispatch(twilioMakeCallFailed()),
    });
  };

  // Agent-to-agent call. No region resolution is needed.
  const handleMakeInternalCall: TwilioActionHandler = (action) => {
    const { userId } = action.payload;

    dispatch(twilioAddActiveCall(userId, 'internal', Date.now()));

    executeTwilioCommand<CallResult>({
      action,
      run: () => runtime.makeInternalCall(userId as string),
      onSucceeded: (result) =>
        dispatch(
          twilioMakeInternalCallSucceeded({
            status: result.status,
            direction: result.direction,
            userId,
          }),
        ),
      onFailed: () => dispatch(twilioMakeInternalCallFailed()),
    });
  };

  // Callback on an existing conversation. Region is derived from the
  // conversation (csid) rather than a phone number because the conversation
  // already carries the region it was originally routed through.
  const handleMakeCallback: TwilioActionHandler = (action) => {
    const csid = readCsidFromAction({
      action,
      context: 'MakeCallback',
      onRejected: (payload) => dispatch(twilioMakeCallbackFailed(payload)),
    });

    if (csid == null) {
      return;
    }

    executeRegionAwareTwilioCommand<CallResult>({
      action,
      resolveRegion: () =>
        resolveConversationLedTokenRegion(csid, 'makeCallback'),
      onMissingRegion: () =>
        dispatch(twilioMakeCallbackFailed({ reason: missingRegionReason })),
      beforeRun: () =>
        dispatch(twilioAddActiveCall(csid, 'callback', Date.now())),
      run: () => runtime.makeCallback(csid),
      onSucceeded: (result) =>
        dispatch(
          twilioMakeCallbackSucceeded({
            status: result.status,
            csid: result.csid,
          }),
        ),
      onFailed: () => dispatch(twilioMakeCallbackFailed()),
    });
  };

  // Join an in-progress call. Region strategy depends on call type:
  // - Internal (agent-to-agent) calls have no provisioned PSTN number, so a
  //   region cannot be derived from a phone number. The receiver dials into
  //   the existing conference using whatever region its device is already
  //   registered in (matches pre-region-aware legacy behaviour).
  // - PSTN calls resolve the region from the conversation's provisioned
  //   number so the device is on the matching edge before dialling in.
  //
  // Routing both through executeRegionAwareTwilioCommand would cause internal
  // calls to fail region resolution (no provisioned_number), surface a
  // misleading "phone number region is unavailable" banner, and leave the
  // caller alone in the Twilio conference because the receiver never joins.
  const handleConnectCall: TwilioActionHandler = (action) => {
    const csid = readCsidFromAction({
      action,
      context: 'ConnectCall',
      onRejected: (payload) => dispatch(twilioConnectCallFailed(payload)),
    });

    if (csid == null) {
      return;
    }

    const isInternal = action.payload?.isInternal === true;

    const runConnect = (): Promise<CallResult> =>
      runtime.connectCall({
        csid,
        muted: action.payload.muted as boolean | undefined,
        isOnlyListening: action.payload.isOnlyListening as boolean | undefined,
      });

    const onSucceeded = (result: CallResult): void => {
      dispatch(
        twilioConnectCallSucceeded({
          status: result.status,
          direction: result.direction,
          csid: result.csid,
        }),
      );
    };

    const onFailed = (): void => {
      dispatch(twilioConnectCallFailed());
    };

    if (isInternal) {
      // Bypass region resolution: keep the device on its current edge.
      executeTwilioCommand<CallResult>({
        action,
        run: runConnect,
        onSucceeded,
        onFailed,
      });
      return;
    }

    executeRegionAwareTwilioCommand<CallResult>({
      action,
      resolveRegion: () =>
        resolveConversationLedTokenRegion(csid, 'connectCall'),
      onMissingRegion: () =>
        dispatch(twilioConnectCallFailed({ reason: missingRegionReason })),
      run: runConnect,
      onSucceeded,
      onFailed,
    });
  };

  const handleDisconnectCall: TwilioActionHandler = (action) => {
    if (
      readCsidFromAction({
        action,
        context: 'DisconnectCall',
        onRejected: (payload) => dispatch(twilioDisconnectCallFailed(payload)),
      }) == null
    ) {
      return;
    }

    executeTwilioCommand({
      action,
      run: () => runtime.disconnectCall(),
      onSucceeded: (result) =>
        dispatch(twilioDisconnectCallSucceeded({ status: result.status })),
      onFailed: () => dispatch(twilioDisconnectCallFailed()),
    });
  };

  const handleFeedback: TwilioActionHandler = (action) => {
    executeTwilioCommand({
      action,
      run: () =>
        runtime.sendFeedback({
          score: action.payload.selectedStar as Call.FeedbackScore,
          issue: action.payload.specificIssue as Call.FeedbackIssue | null,
        }),
      onSucceeded: (result) => handlers.onFeedbackSucceeded(result),
      onFailed: (error) => handlers.onFeedbackFailed(error),
      // Feedback failures are non-critical UX; skip Sentry noise for them.
      captureException: false,
    });
  };

  // Action-type → handler map consumed by the middleware's dispatch interceptor.
  // Each key is the string representation of a Twilio action creator so the
  // lookup is an O(1) dictionary access rather than a chain of if/else checks.
  return {
    [twilioInit.toString()]: (action) => {
      executeTwilioCommand<void>({
        action,
        run: () => runtime.initializeDevice(),
        onSucceeded: () => {},
        onFailed: () => {},
      });
    },
    [twilioMakeCall.toString()]: handleMakeCall,
    [twilioMakeInternalCall.toString()]: handleMakeInternalCall,
    [twilioMakeCallback.toString()]: handleMakeCallback,
    [twilioConnectCall.toString()]: handleConnectCall,
    [twilioDisconnectCall.toString()]: handleDisconnectCall,
    [twilioSendDigits.toString()]: (action) => {
      runtime.sendDigits(action.payload.digit as string);
    },
    [twilioFeedback.toString()]: handleFeedback,
    [twilioClose.toString()]: () => {
      resetRegion();
      runtime.shutdown();
    },
  };
}
